/**
 * Why an image job failed — a dismissable modal opened from a failed Images
 * row.
 *
 * Failure text belongs here and nowhere else: the reasons are full sentences
 * ("Content policy violation: …", the reaper's 1-hour timeout message), and
 * inlining them into a list row is what made the Jobs panel unreadable on a
 * phone. The row stays a title; the detail is one tap away.
 *
 * Mirrors ConfirmDialog's conventions (focus trap, Escape, backdrop click) but
 * carries a single dismiss action — there is nothing to confirm.
 */

import { useEffect, useRef } from 'react';

import type { ImageJob } from '../../agents/imageJobsApi.ts';
import './ImageFailureDialog.css';

interface ImageFailureDialogProps {
  /** The failed job to explain; null renders nothing. */
  job: ImageJob | null;
  onClose: () => void;
}

/** Local-time failure stamp, or null when the record carries no usable time. */
function failedAtLabel(job: ImageJob): string | null {
  const raw = job.updatedAt ?? job.createdAt;
  if (raw === undefined) {
    return null;
  }
  const at = Date.parse(raw);
  if (!Number.isFinite(at)) {
    return null;
  }
  return new Date(at).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function ImageFailureDialog({ job, onClose }: ImageFailureDialogProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (job === null) {
      return;
    }
    restoreRef.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      restoreRef.current?.focus();
    };
  }, [job, onClose]);

  if (job === null) {
    return null;
  }

  const when = failedAtLabel(job);

  return (
    <div
      className="om-imgfail__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="om-imgfail" role="dialog" aria-modal="true" aria-labelledby="om-imgfail-title">
        <h2 className="om-imgfail__title" id="om-imgfail-title">
          This image couldn’t be generated
        </h2>

        <p className="om-imgfail__reason">
          {job.error !== undefined && job.error.length > 0
            ? job.error
            : 'No reason was recorded for this failure. If it keeps happening, try rewording the prompt or ask again later.'}
        </p>

        <dl className="om-imgfail__meta">
          {job.prompt !== undefined && job.prompt.length > 0 ? (
            <>
              <dt>Prompt</dt>
              <dd>{job.prompt}</dd>
            </>
          ) : null}
          {job.model !== undefined ? (
            <>
              <dt>Model</dt>
              <dd>{job.model}</dd>
            </>
          ) : null}
          {when !== null ? (
            <>
              <dt>Failed</dt>
              <dd>{when}</dd>
            </>
          ) : null}
        </dl>

        <div className="om-imgfail__actions">
          <button type="button" className="om-imgfail__btn" onClick={onClose} ref={closeRef}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
